import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail, MapPin, Phone } from 'lucide-react';
import { footerLinks, site } from '../../data/site';
import { certifications } from '../../data/certifications';
import Logo from './Logo';
import Container from '../ui/Container';

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden bg-ink-950 text-ink-300">
      {/* Subtle grid + glow backdrop */}
      <div className="pointer-events-none absolute inset-0 bg-grid bg-grid-cell opacity-60" aria-hidden="true" />
      <div
        className="pointer-events-none absolute -left-32 top-0 h-80 w-80 rounded-full bg-brand-600/20 blur-3xl"
        aria-hidden="true"
      />

      <Container className="relative">
        {/* CTA strip */}
        <div className="flex flex-col gap-6 border-b border-white/10 py-12 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
              Ready to clear your e-waste, compliantly?
            </h2>
            <p className="mt-2 max-w-xl text-sm text-ink-400">
              Tell us what you have. We will come back with a quotation and a pickup slot within 24 hours.
            </p>
          </div>
          <Link
            to="/request-pickup"
            className="btn shrink-0 bg-brand-500 px-7 py-3.5 text-white transition hover:bg-brand-400"
          >
            Request a Pickup
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Main columns */}
        <div className="grid gap-10 py-14 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Logo tone="dark" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-ink-400">
              Green Circuit Recycling is an end-to-end IT Asset Disposition (ITAD) partner. We serve enterprises across India by routing all material exclusively through our closed network of CPCB-authorised recyclers, ensuring maximized value recovery, certified data destruction, and audit-ready statutory documentation.
            </p>

            <div className="mt-6 space-y-3 text-sm">
              <p className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-brand-400" />
                <span className="text-ink-400">{site.addressLines.join(', ')}</span>
              </p>
              <a
                href={`tel:${site.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-3 text-ink-400 transition hover:text-white"
              >
                <Phone className="h-4 w-4 flex-none text-brand-400" />
                {site.phone}
              </a>
              <a
                href={`mailto:${site.email}`}
                className="flex items-center gap-3 text-ink-400 transition hover:text-white"
              >
                <Mail className="h-4 w-4 flex-none text-brand-400" />
                {site.email}
              </a>
            </div>
          </div>

          {footerLinks.map((column) => (
            <div key={column.title}>
              <h3 className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-white">
                {column.title}
              </h3>
              <ul className="mt-5 space-y-3 text-sm">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-ink-400 transition-colors hover:text-brand-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Certification strip */}
        <div className="flex flex-wrap gap-2 border-t border-white/10 py-6">
          {certifications.map((cert) => (
            <span
              key={cert.id}
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[0.7rem] font-semibold uppercase tracking-wider text-ink-300"
            >
              {cert.code}
            </span>
          ))}
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 py-6 text-xs text-ink-500 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {year} {site.legalName}. All rights reserved.
          </p>
          <p className="text-ink-600">
          </p>
        </div>
      </Container>
    </footer>
  );
}
